import React from 'react';
import { useUserRole } from '../../hooks/useUserRole';
import { Tooltip } from '@mui/material';
import { useTranslate } from '../../locales';
import { useAppSelector } from '../../app/hooks';
import { UserOtherPrivilegesEnum, OtherPrivilegeDetail } from '../../modules/auth/userOtherPrivilegesSlice';
import { PrivilegeLevel } from 'src/enums/PrivilegeLevel';

interface OtherPrivilegeGuardProps {
  children: React.ReactNode;
  uopmid?: UserOtherPrivilegesEnum;
  privid?: number;
  fallback?: React.ReactNode;
  modifyPriv?: string | PrivilegeLevel;
  onCheck?: (privilege: OtherPrivilegeDetail) => boolean;
  owner?: string | null;
  extraAccess?: boolean;
}

export const checkModifyPrivilege = (
  requiredPriv: string,
  privilege: OtherPrivilegeDetail,
  username: string | null,
  owner: string | null,
  extraAccess: boolean = false
): boolean => {
  const userPriv = privilege.modifypriv;

  if (userPriv === PrivilegeLevel.MedcardEditAll) return true;
  if (requiredPriv === PrivilegeLevel.MedcardEditOnlyUser) {
      if (userPriv === PrivilegeLevel.MedcardEditOnlyUser || userPriv === PrivilegeLevel.MedcardEditWithBranch) {
          if (owner) {
              if (username && owner.toLowerCase() === username.toLowerCase()) return true;
              if (userPriv === PrivilegeLevel.MedcardEditWithBranch && extraAccess) return true;
              return false;
          }
          return true;
      }
      return false;
  }

  return userPriv === requiredPriv;
};

/**
 * OtherPrivilegeGuard componenti açıklaması:
 * Nesnelerinizi bu component ile sarmalayarak yetkiye bağlı davranışını kontrol ediyoruz.
 * 
 * Kullanıcının belirli bir 'diğer yetkiye' (örneğin, belirli Tıbbi Kart erişimi) sahip olup olmadığını kontrol eder.
 * 
 * @param uopmid - Kullanıcı Diğer Yetki Modül Kimliği (örneğin, MedicalCards = 1)
 * @param privid - Belirli yetki Kimliği (örneğin, bir kart için orId)
 * @param modifyPriv - Gerekli düzenleme yetki seviyesi (isteğe bağlı)
 * @param onCheck - Yetkiyi doğrulamak için özel geri çağırma (isteğe bağlı, yetki nesnesini alır)
 * @param fallback - Yetkisiz ise render edilecek içerik (isteğe bağlı)
 * @param children - Yetkili ise render edilecek içerik
 * @param extraAccess - Ekstra erişim izni (Doğrudan true verilirse yetki kontrolünü geçer)
 */
export const OtherPrivilegeGuard: React.FC<OtherPrivilegeGuardProps> = ({
  children,
  uopmid,
  privid,
  fallback = null,
  modifyPriv,
  onCheck,
  owner,
  extraAccess,
}) => {
  const { hasOtherPrivilege, getOtherPrivilege, loading } = useUserRole();
  const { user } = useAppSelector((state) => state.auth);
  const { t } = useTranslate();

  if (loading) {
    return <>{fallback}</>; 
  }

  // Eğer uopmid yoksa ve extraAccess varsa, direkt extraAccess'e güven
  if (uopmid === undefined && extraAccess !== undefined) {
    return extraAccess ? (
      <>{children}</>
    ) : (
      fallback ? (
        <Tooltip title={t("no-permission-for-this-action")} arrow>
          <span>{fallback}</span>
        </Tooltip>
      ) : null
    );
  }

  // Normal akış (uopmid varsa)
  if (uopmid !== undefined && privid !== undefined) {
    let hasPermission = hasOtherPrivilege(uopmid, privid);

    if (hasPermission && modifyPriv) {
       const privilege = getOtherPrivilege(uopmid, privid);
       if (onCheck) {
         if (!privilege || !onCheck(privilege)) {
           hasPermission = false;
         }
       } else if (privilege && !checkModifyPrivilege(modifyPriv, privilege, user?.username || null, owner || null, extraAccess)) {
          hasPermission = false;
       }
    }

    return hasPermission ? (
      <>{children}</>
    ) : (
      fallback ? (
        <Tooltip title={t("no-permission-for-this-action")} arrow>
          <span>{fallback}</span>
        </Tooltip>
      ) : null
    );
  }

  return fallback ? (
    <Tooltip title={t("no-permission-for-this-action")} arrow>
      <span>{fallback}</span>
    </Tooltip>
  ) : null;
};
